import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Field from '../../components/Field';
import { Shield, UserPlus, X, Key, Trash2, Edit } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const BASE = `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/api/v1/public/admins`;

const EMPTY = { id: null, username: '', email: '', password: '', is_superuser: false };

export default function AdminManager() {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(null); // 'create' | 'edit' | 'password'
  const [form, setForm] = useState(EMPTY);
  const [pwForm, setPwForm] = useState({ id: null, name: '', newPw: '', confirm: '' });


  const fetchAdmins = async () => {
    try {
      const res = await axios.get(`${BASE}/`);
      setAdmins(res.data);
    } catch {
      setError('Error al cargar administradores.');
    }
  };

  useEffect(() => { fetchAdmins(); }, []);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  const openCreate = () => { setForm(EMPTY); setError(null); setModal('create'); };
  const openEdit = (a) => { setForm({ id: a.id, username: a.username, email: a.email, password: '', is_superuser: a.is_superuser }); setError(null); setModal('edit'); };
  const openPw = (a) => { setPwForm({ id: a.id, name: a.username, newPw: '', confirm: '' }); setError(null); setModal('password'); };

  const handleCreate = async (e) => {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      await axios.post(`${BASE}/`, { username: form.username, email: form.email, password: form.password, is_superuser: form.is_superuser });
      setModal(null);
      fetchAdmins();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al crear el administrador.');
    } finally { setLoading(false); }
  };

  const handleEdit = async (e) => {
    e.preventDefault();
    setLoading(true); setError(null);
    try {
      await axios.patch(`${BASE}/${form.id}/`, { username: form.username, email: form.email, is_superuser: form.is_superuser });
      setModal(null);
      fetchAdmins();
    } catch (err) {
      setError(err.response?.data?.error || 'Error al actualizar.');
    } finally { setLoading(false); }
  };

  const handlePwChange = async (e) => {
    e.preventDefault();
    if (pwForm.newPw !== pwForm.confirm) { setError('Las contraseñas no coinciden.'); return; }
    setLoading(true); setError(null);
    try {
      await axios.patch(`${BASE}/${pwForm.id}/`, { password: pwForm.newPw });
      setModal(null);
      alert(`Contraseña de "${pwForm.name}" actualizada.`);
    } catch {
      setError('Error al cambiar la contraseña.');
    } finally { setLoading(false); }
  };

  const handleToggle = async (a) => {
    if (!window.confirm(`¿${a.is_active ? 'Desactivar' : 'Activar'} a "${a.username}"?`)) return;
    try {
      await axios.patch(`${BASE}/${a.id}/`, { is_active: !a.is_active });
      fetchAdmins();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al cambiar estado.');
    }
  };

  const handleDelete = async (a) => {
    if (!window.confirm(`¿Eliminar a "${a.username}"? Esta acción no se puede deshacer.`)) return;
    try {
      await axios.delete(`${BASE}/${a.id}/`);
      fetchAdmins();
    } catch (err) {
      alert(err.response?.data?.error || 'Error al eliminar.');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center bg-white p-6 rounded-[2.5rem] shadow-sm border border-slate-100">
        <div className="flex items-center gap-4">
          <div className="bg-brand-50 p-4 rounded-2xl text-brand-800"><Shield size={24} /></div>
          <div>
            <h2 className="titulo-modal">Administradores de Plataforma</h2>
            <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">Acceso central al panel SaaS</p>
          </div>
        </div>
        <button
          onClick={openCreate}
          className="bg-slate-900 text-white px-5 py-2.5 rounded-xl font-black text-xs uppercase tracking-widest hover:bg-slate-700 shadow-lg flex items-center gap-2"
        >
          <UserPlus size={16} /> Nuevo Admin
        </button>
      </div>

      {error && !modal && (
        <div className="bg-error-50 text-error-600 text-sm font-medium p-4 rounded-xl border border-error-100">{error}</div>
      )}

      <div className="bg-white rounded-[2.5rem] shadow-sm border border-slate-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="bg-slate-50 text-slate-500 border-b border-slate-100">
                <th className="tabla-encabezado">Usuario</th>
                <th className="tabla-encabezado">Email</th>
                <th className="tabla-encabezado">Rol</th>
                <th className="tabla-encabezado">Alta</th>
                <th className="px-6 py-4 font-bold uppercase tracking-wider text-[11px] text-right">Estado / Acciones</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 font-medium">
              {admins.length === 0 && (
                <tr><td colSpan={5} className="text-center py-10 text-slate-400 text-sm">Sin administradores registrados.</td></tr>
              )}
              {admins.map(a => (
                <tr key={a.id} className="hover:bg-slate-50/50 transition-colors">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 font-black text-xs">
                        {a.username[0].toUpperCase()}
                      </div>
                      <span className="font-bold text-slate-800">{a.username}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-slate-500">{a.email}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 rounded-full text-[10px] font-black uppercase border ${a.is_superuser ? 'bg-brand-50 text-brand-800 border-brand-100' : 'bg-slate-100 text-slate-600 border-slate-200'}`}>
                      {a.is_superuser ? 'Super Admin' : 'Staff'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-xs text-slate-400">{a.date_joined}</td>
                  <td className="px-6 py-4">
                    <div className="flex justify-end items-center gap-2">
                      <button onClick={() => openPw(a)} className="p-2 text-slate-300 hover:text-alerta-500 transition-colors" title="Cambiar contraseña">
                        <Key size={15} />
                      </button>
                      <button onClick={() => openEdit(a)} className="accion-fila" title="Editar">
                        <Edit size={15} />
                      </button>
                      <button onClick={() => handleDelete(a)} className="p-2 text-slate-300 hover:text-error-500 transition-colors" title="Eliminar">
                        <Trash2 size={15} />
                      </button>
                      <button
                        onClick={() => handleToggle(a)}
                        className={`px-3 py-1 rounded-full text-[10px] font-black tracking-widest border ${a.is_active
                          ? 'border-exito-500/30 text-exito-600 bg-exito-50'
                          : 'border-error-500/30 text-error-600 bg-error-50'
                        }`}
                      >
                        {a.is_active ? 'ACTIVO' : 'INACTIVO'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Modal crear */}
      <AnimatePresence>
        {modal === 'create' && (
          <Overlay onClose={() => setModal(null)}>
            <h3 className="text-2xl font-black text-slate-800 mb-6">Nuevo Administrador</h3>
            {error && <ErrBox msg={error} />}
            <form onSubmit={handleCreate} className="space-y-4">
              <F label="Usuario *"><input required className={IC} value={form.username} onChange={e => set('username', e.target.value)} /></F>
              <F label="Email *"><input required type="email" className={IC} value={form.email} onChange={e => set('email', e.target.value)} /></F>
              <F label="Contraseña *"><input required type="password" minLength={6} className={IC} value={form.password} onChange={e => set('password', e.target.value)} /></F>
              <button
                type="button"
                role="switch"
                aria-checked={form.is_superuser}
                onClick={() => set('is_superuser', !form.is_superuser)}
                className="flex items-center gap-3 cursor-pointer select-none text-left"
              >
                <span className={`block w-10 h-5 rounded-full transition-colors relative ${form.is_superuser ? 'bg-brand-600' : 'bg-slate-200'}`}>
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${form.is_superuser ? 'translate-x-5' : ''}`} />
                </span>
                <span className="text-sm font-bold text-slate-600">Super Admin (acceso total)</span>
              </button>
              <Buttons onCancel={() => setModal(null)} loading={loading} label="Crear Admin" />
            </form>
          </Overlay>
        )}
      </AnimatePresence>

      {/* Modal editar */}
      <AnimatePresence>
        {modal === 'edit' && (
          <Overlay onClose={() => setModal(null)}>
            <h3 className="text-2xl font-black text-slate-800 mb-6">Editar Administrador</h3>
            {error && <ErrBox msg={error} />}
            <form onSubmit={handleEdit} className="space-y-4">
              <F label="Usuario"><input required className={IC} value={form.username} onChange={e => set('username', e.target.value)} /></F>
              <F label="Email"><input required type="email" className={IC} value={form.email} onChange={e => set('email', e.target.value)} /></F>
              <button
                type="button"
                role="switch"
                aria-checked={form.is_superuser}
                onClick={() => set('is_superuser', !form.is_superuser)}
                className="flex items-center gap-3 cursor-pointer select-none text-left"
              >
                <span className={`block w-10 h-5 rounded-full transition-colors relative ${form.is_superuser ? 'bg-brand-600' : 'bg-slate-200'}`}>
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-white shadow transition-transform ${form.is_superuser ? 'translate-x-5' : ''}`} />
                </span>
                <span className="text-sm font-bold text-slate-600">Super Admin</span>
              </button>
              <Buttons onCancel={() => setModal(null)} loading={loading} label="Guardar Cambios" />
            </form>
          </Overlay>
        )}
      </AnimatePresence>

      {/* Modal cambiar contraseña */}
      <AnimatePresence>
        {modal === 'password' && (
          <Overlay onClose={() => setModal(null)}>
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-alerta-50 p-3 rounded-xl text-alerta-500"><Key size={20} /></div>
              <div>
                <h3 className="text-xl font-black text-slate-800">Cambiar Contraseña</h3>
                <p className="text-xs text-slate-400">{pwForm.name}</p>
              </div>
            </div>
            {error && <ErrBox msg={error} />}
            <form onSubmit={handlePwChange} className="space-y-4">
              <F label="Nueva Contraseña *"><input required type="password" minLength={6} className={IC} value={pwForm.newPw} onChange={e => setPwForm(p => ({ ...p, newPw: e.target.value }))} /></F>
              <F label="Confirmar Contraseña *"><input required type="password" className={IC} value={pwForm.confirm} onChange={e => setPwForm(p => ({ ...p, confirm: e.target.value }))} /></F>
              <Buttons onCancel={() => setModal(null)} loading={loading} label="Actualizar" />
            </form>
          </Overlay>
        )}
      </AnimatePresence>
    </div>
  );
}

function Overlay({ children, onClose }) {
  return (
    <div className="fondo-modal">
      <motion.div
        initial={{ opacity: 0, scale: 0.97 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.97 }}
        className="bg-white rounded-[2.5rem] w-full max-w-sm shadow-2xl p-8 relative"
      >
        <button onClick={onClose} className="absolute top-6 right-6 text-slate-300 hover:text-slate-500"><X size={20} /></button>
        {children}
      </motion.div>
    </div>
  );
}

const F = Field;

function ErrBox({ msg }) {
  return <div className="aviso-error mb-4">{msg}</div>;
}

function Buttons({ onCancel, loading, label }) {
  return (
    <div className="flex gap-3 pt-2">
      <button type="button" onClick={onCancel} className="flex-1 bg-slate-100 text-slate-600 font-black uppercase tracking-widest py-3 rounded-2xl text-xs hover:bg-slate-200">Cancelar</button>
      <button disabled={loading} className="flex-1 bg-slate-900 text-white font-black uppercase tracking-widest py-3 rounded-2xl text-xs disabled:opacity-50">{loading ? '...' : label}</button>
    </div>
  );
}

const IC = 'campo';
